import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';
import { useGetPerformanceSolvency } from 'services/calculations/financialPerformance/hooks';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

const hasAllPerformanceSolvencyFields = (requestData: Record<string, unknown>) => {
  return Object.values(requestData).every((value) => value !== undefined && value !== null);
};

export const usePerformanceSolvency = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getPerformanceSolvency = useGetPerformanceSolvency();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [performanceSolvencyFiled] = observedFields.performanceSolvency.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (requestData, unit) => {
        if (!hasAllPerformanceSolvencyFields(requestData)) return;

        const params = {
          unit,
          businessPlanId,
          requestData,
        };

        try {
          const {
            debtToEquityRatio,
            totalAssetsToEquityRatio,
            totalAssetsToTotalLiabilitiesRatio,
            capitalizationRatio,
            interestCoverageRatio,
          } = await getPerformanceSolvency.mutateAsync(params);

          setValue(performanceSolvencyFiled, [
            debtToEquityRatio,
            totalAssetsToEquityRatio,
            totalAssetsToTotalLiabilitiesRatio,
            capitalizationRatio,
            interestCoverageRatio,
          ]);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 600),
    [],
  );

  const performanceSolvencyHandler = useCallback(
    async (content: IFormContentTypes['performanceSolvency']) => {
      if (!canAccess) return;
      const [
        totalCurrentLiabilities,
        totalNonCurrentLiabilities,
        totalStockholderEquity,
        totalAsset,
        amortizationData,
        profitabilityTable,
        interestPaid,
        operationUnit,
      ] = content;

      const ebitda = profitabilityTable[2];

      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      const performanceSolvencyData = {
        totalCurrentLiabilities,
        totalNonCurrentLiabilities,
        totalStockholderEquity,
        totalAsset,
        amortizationData,
        ebitda,
        interestPaid,
      };

      debouncedUpdateFunction(performanceSolvencyData, unit);
    },
    [debouncedUpdateFunction],
  );

  return performanceSolvencyHandler;
};
